import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  X,
  Check,
  Loader2,
  Search,
  Building2,
  User,
  Package,
  Layers,
  Users2,
  Calendar,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Filter,
  AlertCircle,
} from "lucide-react";
import { colors, fonts } from "../styles/theme";
import {
  fetchCatalog,
  fetchEntitlements,
  createEntitlement,
  updateEntitlement,
  cancelEntitlement,
  fetchContacts,
  fetchCompanies,
} from "../services/api";
import { useTenant } from "../contexts/TenantContext";

// ── Styles ────────────────────────────────────────────────────

const card = {
  background: colors.bgCard,
  border: `1px solid ${colors.border}`,
  borderRadius: 10,
  padding: 20,
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  fontSize: 13,
  fontFamily: fonts.body,
  background: colors.bg,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  color: colors.text,
  outline: "none",
  boxSizing: "border-box",
};

const selectStyle = { ...inputStyle, cursor: "pointer" };

const btnPrimary = {
  padding: "8px 18px",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fonts.body,
  background: colors.accent,
  color: "#ffffff",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const btnSecondary = {
  ...btnPrimary,
  background: "transparent",
  color: colors.textSecondary,
  border: `1px solid ${colors.border}`,
};

const btnDanger = {
  ...btnPrimary,
  background: "rgba(239,68,68,0.15)",
  color: "#ef4444",
};

const CURRENCY_SYMBOLS = { gbp: "£", usd: "$", eur: "€" };

function formatPrice(pence, currency = "gbp") {
  if (pence == null) return "—";
  const sym = CURRENCY_SYMBOLS[currency] || "£";
  return `${sym}${(pence / 100).toFixed(2)}`;
}

const STATUS_COLORS = {
  active: { bg: "rgba(16,185,129,0.1)", color: "#10b981" },
  cancelled: { bg: "rgba(239,68,68,0.1)", color: "#ef4444" },
  expired: { bg: "rgba(245,158,11,0.1)", color: "#f59e0b" },
  pending: { bg: "rgba(99,102,241,0.1)", color: "#6366f1" },
};

// ── Main Component ────────────────────────────────────────────

export default function ClientAccess() {
  const tenant = useTenant();
  const [catalog, setCatalog] = useState({
    products: [],
    bundles: [],
    clientTypes: [],
  });
  const [entitlements, setEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEntitlement, setEditingEntitlement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState("all"); // all, contact, company
  const [filterStatus, setFilterStatus] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const loadCatalog = useCallback(async () => {
    const data = await fetchCatalog();
    const products = [],
      bundles = [],
      clientTypes = [];
    for (const item of data.items || []) {
      if (item.active === false) continue;
      if (item.docType === "product") products.push(item);
      else if (item.docType === "bundle") bundles.push(item);
      else if (item.docType === "client_type") clientTypes.push(item);
    }
    setCatalog({ products, bundles, clientTypes });
  }, []);

  const loadEntitlements = useCallback(async () => {
    const params = {};
    if (filterType !== "all") params.entityType = filterType;
    if (filterStatus) params.status = filterStatus;
    const data = await fetchEntitlements(params);
    setEntitlements(data.entitlements || []);
  }, [filterType, filterStatus]);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([loadCatalog(), loadEntitlements()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loadCatalog, loadEntitlements]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleCreate = async (formData) => {
    setSaving(true);
    try {
      await createEntitlement(formData);
      setShowForm(false);
      await loadEntitlements();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, formData) => {
    setSaving(true);
    try {
      await updateEntitlement(id, formData);
      setEditingEntitlement(null);
      await loadEntitlements();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (ent) => {
    if (!confirm(`Cancel entitlement for "${ent.entityName}"?`)) return;
    try {
      await cancelEntitlement(ent.id, ent.entityId);
      await loadEntitlements();
    } catch (err) {
      setError(err.message);
    }
  };

  // Resolve product names for an entitlement
  const resolveProducts = (ent) => {
    const productIds = new Set(ent.productIds || []);
    // Add products from client type
    if (ent.clientTypeId) {
      const ct = catalog.clientTypes.find((c) => c.id === ent.clientTypeId);
      if (ct)
        (ct.includedProductIds || []).forEach((pid) => productIds.add(pid));
    }
    // Add products from bundles
    for (const bid of ent.bundleIds || []) {
      const bundle = catalog.bundles.find((b) => b.id === bid);
      if (bundle)
        (bundle.productIds || []).forEach((pid) => productIds.add(pid));
    }
    return [...productIds]
      .map((pid) => catalog.products.find((p) => p.id === pid))
      .filter(Boolean);
  };

  // Filter entitlements by search
  const filtered = entitlements.filter((e) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (e.entityName || "").toLowerCase().includes(q) ||
      (e.entityId || "").toLowerCase().includes(q)
    );
  });

  // Stats
  const stats = {
    total: entitlements.length,
    contacts: entitlements.filter((e) => e.entityType === "contact").length,
    companies: entitlements.filter((e) => e.entityType === "company").length,
  };

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontFamily: fonts.heading,
              fontWeight: 700,
              margin: 0,
              color: colors.text,
            }}
          >
            Client Access
          </h1>
          <p
            style={{ fontSize: 13, color: colors.textSecondary, marginTop: 4 }}
          >
            Manage product access for contacts and companies
          </p>
        </div>
        <button
          style={btnPrimary}
          onClick={() => {
            setShowForm(true);
            setEditingEntitlement(null);
          }}
        >
          <Plus size={15} /> New Entitlement
        </button>
      </div>

      {/* Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          marginBottom: 20,
        }}
      >
        {[
          { label: "Total Entitlements", value: stats.total, icon: Package },
          { label: "Contacts", value: stats.contacts, icon: User },
          { label: "Companies", value: stats.companies, icon: Building2 },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              ...card,
              padding: 16,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                background: colors.accentDim,
                borderRadius: 8,
                padding: 8,
              }}
            >
              <s.icon size={18} color={colors.accent} />
            </div>
            <div>
              <div
                style={{ fontSize: 20, fontWeight: 700, color: colors.text }}
              >
                {s.value}
              </div>
              <div style={{ fontSize: 12, color: colors.textSecondary }}>
                {s.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            ...card,
            borderColor: colors.danger,
            marginBottom: 16,
            padding: 12,
            fontSize: 13,
            color: colors.danger,
          }}
        >
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              float: "right",
              background: "none",
              border: "none",
              color: colors.danger,
              cursor: "pointer",
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
          alignItems: "center",
        }}
      >
        <div style={{ position: "relative", flex: 1 }}>
          <Search
            size={14}
            style={{
              position: "absolute",
              left: 10,
              top: 10,
              color: colors.textSecondary,
            }}
          />
          <input
            style={{ ...inputStyle, paddingLeft: 32 }}
            placeholder="Search by name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          style={{ ...selectStyle, width: 140 }}
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="contact">Contacts</option>
          <option value="company">Companies</option>
        </select>
        <select
          style={{ ...selectStyle, width: 140 }}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="active">Active</option>
          <option value="">All Statuses</option>
          <option value="cancelled">Cancelled</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {/* Form */}
      {(showForm || editingEntitlement) && (
        <EntitlementForm
          entitlement={editingEntitlement}
          catalog={catalog}
          saving={saving}
          currency={tenant.currency || "gbp"}
          onSubmit={
            editingEntitlement
              ? (d) => handleUpdate(editingEntitlement.id, d)
              : handleCreate
          }
          onCancel={() => {
            setShowForm(false);
            setEditingEntitlement(null);
          }}
        />
      )}

      {/* List */}
      {loading ? (
        <div
          style={{ textAlign: "center", padding: 60, color: colors.textSecondary }}
        >
          <Loader2 size={24} className="spin" />
          <p style={{ marginTop: 12, fontSize: 13 }}>Loading entitlements...</p>        </div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: 60 }}>
          <Package
            size={40}
            color={colors.textSecondary}
            style={{ marginBottom: 12 }}
          />
          <p
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            {searchQuery ? "No matching entitlements" : "No entitlements yet"}
          </p>
          <p
            style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 16 }}
          >
            Assign products, bundles, or access tiers to contacts and companies
          </p>
          {!searchQuery && (
            <button style={btnPrimary} onClick={() => setShowForm(true)}>
              <Plus size={15} /> Create Entitlement
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {/* Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "30px 2fr 1fr 2fr 1fr 1fr 80px",
              gap: 12,
              padding: "8px 16px",
              fontSize: 11,
              fontWeight: 600,
              color: colors.textSecondary,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            <span></span>
            <span>Entity</span>
            <span>Access Tier</span>
            <span>Products</span>
            <span>Status</span>
            <span>Start</span>
            <span></span>
          </div>
          {filtered.map((ent) => {
            const isExpanded = expandedId === ent.id;
            const resolved = resolveProducts(ent);
            const clientType = ent.clientTypeId
              ? catalog.clientTypes.find((c) => c.id === ent.clientTypeId)
              : null;
            const statusStyle =
              STATUS_COLORS[ent.status] || STATUS_COLORS.active;
            const EntityIcon = ent.entityType === "company" ? Building2 : User;

            return (
              <div
                key={ent.id}
                style={{ ...card, padding: 0, overflow: "hidden" }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "30px 2fr 1fr 2fr 1fr 1fr 80px",
                    gap: 12,
                    padding: "12px 16px",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => setExpandedId(isExpanded ? null : ent.id)}
                >
                  <span style={{ color: colors.textSecondary }}>
                    {isExpanded ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                  </span>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <EntityIcon size={14} color={colors.textSecondary} />
                    <div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.text,
                        }}
                      >
                        {ent.entityName}
                      </div>
                      <div style={{ fontSize: 11, color: colors.textSecondary }}>
                        {ent.entityType}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: 12, color: colors.textSecondary }}>
                    {clientType?.name || "—"}
                  </span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                    {resolved.slice(0, 4).map((p) => (
                      <span
                        key={p.id}
                        style={{
                          fontSize: 10,
                          background: colors.accentDim,
                          color: colors.accent,
                          padding: "1px 6px",
                          borderRadius: 8,
                          fontWeight: 500,
                        }}
                      >
                        {p.name}
                      </span>
                    ))}
                    {resolved.length > 4 && (
                      <span style={{ fontSize: 10, color: colors.textSecondary }}>
                        +{resolved.length - 4} more
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "3px 10px",
                      borderRadius: 10,
                      background: statusStyle.bg,
                      color: statusStyle.color,
                      display: "inline-block",
                      width: "fit-content",
                    }}
                  >
                    {ent.status}
                  </span>
                  <span style={{ fontSize: 12, color: colors.textSecondary }}>
                    {ent.startDate || "—"}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      gap: 4,
                      justifyContent: "flex-end",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => {
                        setEditingEntitlement(ent);
                        setShowForm(false);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: colors.textSecondary,
                        cursor: "pointer",
                        padding: 4,
                      }}
                    >
                      <Pencil size={13} />
                    </button>
                    {ent.status === "active" && (
                      <button
                        onClick={() => handleCancel(ent)}
                        style={{
                          background: "none",
                          border: "none",
                          color: colors.danger,
                          cursor: "pointer",
                          padding: 4,
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div
                    style={{
                      padding: "0 16px 16px 46px",
                      borderTop: `1px solid ${colors.borderLight}`,
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 16,
                        paddingTop: 12,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: colors.textSecondary,
                            marginBottom: 4,
                          }}
                        >
                          All Resolved Products
                        </div>
                        {resolved.length === 0 ? (
                          <span
                            style={{
                              fontSize: 12,
                              color: colors.textSecondary,
                              fontStyle: "italic",
                            }}
                          >
                            None
                          </span>
                        ) : (
                          resolved.map((p) => (
                            <div
                              key={p.id}
                              style={{
                                fontSize: 12,
                                color: colors.text,
                                padding: "2px 0",
                              }}
                            >
                              {p.name}{" "}
                              <span style={{ color: colors.textSecondary }}>
                                ({p.code})
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: colors.textSecondary,
                            marginBottom: 4,
                          }}
                        >
                          Bundles
                        </div>
                        {(ent.bundleIds || []).length === 0 ? (
                          <span
                            style={{
                              fontSize: 12,
                              color: colors.textSecondary,
                              fontStyle: "italic",
                            }}
                          >
                            None
                          </span>
                        ) : (
                          (ent.bundleIds || []).map((bid) => {
                            const b = catalog.bundles.find((x) => x.id === bid);
                            return (
                              <div
                                key={bid}
                                style={{
                                  fontSize: 12,
                                  color: colors.text,
                                  padding: "2px 0",
                                }}
                              >
                                {b?.name || bid}
                              </div>
                            );
                          })
                        )}
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: colors.textSecondary,
                            marginBottom: 4,
                          }}
                        >
                          Details
                        </div>
                        <div
                          style={{ fontSize: 12, color: colors.textSecondary }}
                        >
                          Start: {ent.startDate || "—"}
                        </div>
                        <div
                          style={{ fontSize: 12, color: colors.textSecondary }}
                        >
                          End: {ent.endDate || "Ongoing"}
                        </div>
                        {ent.notes && (
                          <div
                            style={{
                              fontSize: 12,
                              color: colors.text,
                              marginTop: 4,
                            }}
                          >
                            {ent.notes}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 11,
                            color: colors.textSecondary,
                            marginTop: 4,
                          }}
                        >
                          Created by {ent.createdBy}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Entitlement Form ──────────────────────────────────────────

function EntitlementForm({
  entitlement,
  catalog,
  saving,
  currency,
  onSubmit,
  onCancel,
}) {
  const isEdit = !!entitlement;
  const [entityType, setEntityType] = useState(
    entitlement?.entityType || "contact",
  );
  const [entitySearch, setEntitySearch] = useState("");
  const [entityId, setEntityId] = useState(entitlement?.entityId || "");
  const [entityName, setEntityName] = useState(entitlement?.entityName || "");
  const [clientTypeId, setClientTypeId] = useState(
    entitlement?.clientTypeId || "",
  );
  const [selectedProductIds, setSelectedProductIds] = useState(
    entitlement?.productIds || [],
  );
  const [selectedBundleIds, setSelectedBundleIds] = useState(
    entitlement?.bundleIds || [],
  );
  const [startDate, setStartDate] = useState(
    entitlement?.startDate || new Date().toISOString().slice(0, 10),
  );
  const [endDate, setEndDate] = useState(entitlement?.endDate || "");
  const [notes, setNotes] = useState(entitlement?.notes || "");
  const [status, setStatus] = useState(entitlement?.status || "active");

  // Entity search
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!entitySearch || entitySearch.length < 2 || isEdit) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        if (entityType === "contact") {
          const data = await fetchContacts({ search: entitySearch, limit: 10 });
          setSearchResults(
            (data.contacts || []).map((c) => ({
              id: c.email || c.id,
              name:
                `${c.firstName || ""} ${c.lastName || ""}`.trim() || c.email,
              subtitle: c.company || c.email,
            })),
          );
        } else {
          const data = await fetchCompanies({ search: entitySearch });
          setSearchResults(
            (data.companies || []).map((c) => ({
              id: c.name,
              name: c.name,
              subtitle: `${c.contacts?.length || 0} contacts`,
            })),
          );
        }
        setShowDropdown(true);
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [entitySearch, entityType, isEdit]);

  const selectEntity = (result) => {
    setEntityId(result.id);
    setEntityName(result.name);
    setEntitySearch(result.name);
    setShowDropdown(false);
  };

  // Preview: which products will be granted
  const resolvedProducts = (() => {
    const pids = new Set(selectedProductIds);
    if (clientTypeId) {
      const ct = catalog.clientTypes.find((c) => c.id === clientTypeId);
      if (ct) (ct.includedProductIds || []).forEach((pid) => pids.add(pid));
    }
    for (const bid of selectedBundleIds) {
      const bundle = catalog.bundles.find((b) => b.id === bid);
      if (bundle) (bundle.productIds || []).forEach((pid) => pids.add(pid));
    }
    return [...pids]
      .map((pid) => catalog.products.find((p) => p.id === pid))
      .filter(Boolean);
  })();

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      entityType,
      entityId,
      entityName,
      clientTypeId: clientTypeId || null,
      productIds: selectedProductIds,
      bundleIds: selectedBundleIds,
      status,
      startDate,
      endDate: endDate || null,
      notes,
    });
  };

  const toggleProduct = (pid) =>
    setSelectedProductIds((prev) =>
      prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid],
    );

  const toggleBundle = (bid) =>
    setSelectedBundleIds((prev) =>
      prev.includes(bid) ? prev.filter((x) => x !== bid) : [...prev, bid],
    );

  return (
    <form onSubmit={handleSubmit} style={{ ...card, marginBottom: 20 }}>
      <h3
        style={{
          fontSize: 15,
          fontWeight: 700,
          fontFamily: fonts.heading,
          margin: "0 0 16px",
          color: colors.text,
        }}
      >
        {isEdit ? "Edit Entitlement" : "New Entitlement"}
      </h3>

      {/* Entity selection */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "150px 1fr",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <label
          style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}
        >
          Entity Type
          <select
            style={selectStyle}
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setEntityId("");
              setEntityName("");
              setEntitySearch("");
            }}
            disabled={isEdit}
          >
            <option value="contact">Contact</option>
            <option value="company">Company</option>
          </select>
        </label>
        <label
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: colors.textSecondary,
            position: "relative",
          }}
        >
          {entityType === "contact" ? "Contact" : "Company"} *
          {isEdit ? (
            <input style={inputStyle} value={entityName} disabled />
          ) : (
            <>
              <div style={{ position: "relative" }}>
                <Search
                  size={14}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: 10,
                    color: colors.textSecondary,
                  }}
                />
                <input
                  style={{ ...inputStyle, paddingLeft: 32 }}
                  placeholder={`Search ${entityType}s...`}
                  value={entitySearch}
                  onChange={(e) => setEntitySearch(e.target.value)}
                  onFocus={() => searchResults.length && setShowDropdown(true)}
                />
                {searching && (
                  <Loader2
                    size={14}
                    style={{
                      position: "absolute",
                      right: 10,
                      top: 10,
                      color: colors.textSecondary,
                    }}
                    className="spin"
                  />
                )}
              </div>
              {showDropdown && searchResults.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  {searchResults.map((r) => (
                    <div
                      key={r.id}
                      onClick={() => selectEntity(r)}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        borderBottom: `1px solid ${colors.borderLight}`,
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = colors.bgHover)
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <div
                        style={{
                          fontSize: 13,
                          color: colors.text,
                          fontWeight: 500,
                        }}
                      >
                        {r.name}
                      </div>
                      <div style={{ fontSize: 11, color: colors.textSecondary }}>
                        {r.subtitle}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {entityId && (
                <div
                  style={{ fontSize: 12, color: colors.success, marginTop: 4 }}
                >
                  <Check size={12} style={{ verticalAlign: "middle" }} />{" "}
                  Selected: {entityName}
                </div>
              )}
            </>
          )}
        </label>
      </div>

      {/* Client Type - quick assignment */}
      <div style={{ marginBottom: 16 }}>
        <label
          style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}
        >
          Access Tier (sets default product access)
        </label>
        <select
          style={{ ...selectStyle, marginTop: 4 }}
          value={clientTypeId}
          onChange={(e) => setClientTypeId(e.target.value)}
        >
          <option value="">— No access tier —</option>
          {catalog.clientTypes.map((ct) => (
            <option key={ct.id} value={ct.id}>
              {ct.name} — {ct.description}
            </option>
          ))}
        </select>
      </div>

      {/* Bundles */}
      {catalog.bundles.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
              marginBottom: 6,
            }}
          >
            Additional Bundles
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {catalog.bundles.map((b) => {
              const selected = selectedBundleIds.includes(b.id);
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => toggleBundle(b.id)}
                  style={{
                    padding: "6px 14px",
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: fonts.body,
                    background: selected ? colors.info : colors.bgHover,
                    color: selected ? "#fff" : colors.textSecondary,
                    border: `1px solid ${selected ? colors.info : colors.border}`,
                    borderRadius: 20,
                    cursor: "pointer",
                  }}
                >
                  {selected && <Check size={12} style={{ marginRight: 4 }} />}
                  <Layers
                    size={12}
                    style={{ marginRight: 4, verticalAlign: "middle" }}
                  />
                  {b.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Additional Products */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: colors.textSecondary,
            marginBottom: 6,
          }}
        >
          Additional Products (beyond access tier / bundles)
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {catalog.products.map((p) => {
            const selected = selectedProductIds.includes(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggleProduct(p.id)}
                style={{
                  padding: "6px 14px",
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: fonts.body,
                  background: selected ? colors.accent : colors.bgHover,
                  color: selected ? "#fff" : colors.textSecondary,
                  border: `1px solid ${selected ? colors.accent : colors.border}`,
                  borderRadius: 20,
                  cursor: "pointer",
                }}
              >
                {selected && <Check size={12} style={{ marginRight: 4 }} />}
                {p.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Preview resolved access */}
      {resolvedProducts.length > 0 && (
        <div
          style={{
            background: colors.bgHover,
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
              marginBottom: 6,
            }}
          >
            <AlertCircle
              size={12}
              style={{ verticalAlign: "middle", marginRight: 4 }}
            />
            Resolved Product Access ({resolvedProducts.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {resolvedProducts.map((p) => (
              <span
                key={p.id}
                style={{
                  fontSize: 11,
                  background: "rgba(16,185,129,0.1)",
                  color: "#10b981",
                  padding: "3px 10px",
                  borderRadius: 10,
                  fontWeight: 600,
                }}
              >
                {p.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Dates and status */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <label
          style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}
        >
          Start Date
          <input
            style={inputStyle}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
        <label
          style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}
        >
          End Date (optional)
          <input
            style={inputStyle}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>
        <label
          style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}
        >
          Status
          <select
            style={selectStyle}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>
      </div>

      {/* Notes */}
      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: colors.textSecondary,
          display: "block",
          marginBottom: 16,
        }}
      >
        Notes
        <textarea
          style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal notes about this entitlement..."
        />
      </label>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" onClick={onCancel} style={btnSecondary}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || (!isEdit && !entityId)}
          style={{
            ...btnPrimary,
            opacity: saving || (!isEdit && !entityId) ? 0.6 : 1,
          }}
        >
          {saving ? (
            <Loader2
              size={14}
              className="spin"
            />
          ) : (
            <Check size={14} />
          )}
          {isEdit ? "Update" : "Create"} Entitlement
        </button>
      </div>
    </form>
  );
}
